import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy } from '@angular/core';

import { MatSelectChange } from '@angular/material/select';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Locale, LocaleService } from '@next/core-lib/i18n';

import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';

import { takeUntil } from 'rxjs/operators';

import { ALL_WEEK_REGISTRATION_FILTERS } from 'src/assets/data/weekRegistrationFilters';

import { SharedModule } from '../../../shared/shared.module';
import {
  Flock,
  FlockModuleState,
  FlockWeekData,
  FlockWeekDataSet,
  WeekRegistrationFilters,
  WeekRegistrationKPIEnum,
  WeekRegistrationKPIItem,
} from '../../models/flock.model';

import { FlockWeekregistrationKpiService } from '../../services/flock-weekregistration-kpi.service';
import * as flockActions from '../../store/actions/flock.actions';
import { getWeekRegistrationFilters } from '../../store/selectors/flock.selectors';

interface DetailedColumn {
  id: string;
  label: string;
  chartable: boolean;
  kpi?: string;
}

type DetailedRow = Record<string, string | boolean | null>;

@Component({
  selector: 'app-flock-overview-week-registration-detailed',
  templateUrl: './flock-overview-week-registration-detailed.component.html',
  styleUrls: ['./flock-overview-week-registration-detailed.component.scss'],
  imports: [MatSelectModule, MatTooltipModule, SharedModule],
})
export class FlockOverviewWeekRegistrationDetailedComponent implements OnChanges, OnDestroy {
  @Input() flock: Flock;
  @Input() flockWeekDataSet: FlockWeekDataSet;

  private readonly destroy$ = new Subject<void>();
  private readonly weekRegistrationFilters$: Observable<WeekRegistrationFilters> =
    this.store.select(getWeekRegistrationFilters);

  private weekRegistrationFilters: WeekRegistrationFilters;
  weekRegistrationSortBy = 'asc';
  locale: Locale;

  columns: DetailedColumn[] = [];
  rows: DetailedRow[] = [];

  constructor(
    private store: Store<FlockModuleState>,
    private localeService: LocaleService,
    private flockWeekregistrationKpiService: FlockWeekregistrationKpiService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
  ) {
    this.weekRegistrationFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((weekRegistrationFilters) => {
        this.weekRegistrationFilters = weekRegistrationFilters;

        this.refreshTable();
      });
    this.locale = this.localeService.getLocale();
  }

  ngOnChanges(): void {
    this.refreshTable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createColumns(): DetailedColumn[] {
    const columns: DetailedColumn[] = [
      { id: 'week', label: this.translate('report.week-details', 'week'), chartable: false },
      {
        id: 'weekStart',
        label: this.translate('report.week-details', 'weekStart'),
        chartable: false,
      },
    ];

    ALL_WEEK_REGISTRATION_FILTERS.kpiFilters.forEach((kpiFilter) => {
      const foundFilter = this.weekRegistrationFilters?.kpiFilters?.find(
        (filter) => kpiFilter === filter,
      );
      if (foundFilter) {
        columns.push(this.createColumn(kpiFilter));
      }
    });

    ALL_WEEK_REGISTRATION_FILTERS.manualInput.forEach((manualInput) => {
      const foundFilter = this.weekRegistrationFilters?.manualInput?.find(
        (filter) => manualInput === filter,
      );
      if (foundFilter) {
        columns.push(this.createColumn(manualInput));
      }
    });

    if (this.weekRegistrationFilters?.isMoultSelected) {
      columns.push({
        id: 'moult',
        label: this.translate('week-registration', 'moult'),
        chartable: false,
      });
    }
    if (this.weekRegistrationFilters?.isRemarksSelected) {
      columns.push({
        id: 'remarks',
        label: this.translate('week-registration', 'remarks'),
        chartable: false,
      });
    }

    return columns;
  }

  private createColumn(kpi: string): DetailedColumn {
    return {
      id: kpi.slice(0, 1).toLowerCase() + kpi.slice(1),
      label: this.translate('report.week-details', kpi),
      chartable: true,
      kpi,
    };
  }

  private refreshTable(): void {
    this.rows = this.createDataSource();
    this.columns = this.createColumns().map((column) => ({
      ...column,
      chartable:
        column.chartable &&
        this.rows.some((row) => row[column.id] !== '' && row[column.id] !== '-'),
    }));
  }

  private createDataSource(): DetailedRow[] {
    if (!this.flock?.weekRegistrations) {
      return [];
    }

    return this.createDataSourceWeekRegistrations();
  }

  private createDataSourceWeekRegistrations(): DetailedRow[] {
    const sortedFlockWeekData = this.flockWeekDataSet.data.slice();
    if (this.weekRegistrationSortBy === 'desc') {
      sortedFlockWeekData.sort((w1, w2) => (w1.week < w2.week ? 1 : -1));
    } else {
      sortedFlockWeekData.sort((w1, w2) => (w1.week > w2.week ? 1 : -1));
    }

    return sortedFlockWeekData.map((flockWeekData: FlockWeekData) => {
      const weekDataKPIs = this.flockWeekregistrationKpiService.getAllWeekKpi(
        flockWeekData,
        this.flockWeekDataSet.dataSeries,
      );

      return {
        isFirst: false,
        week: weekDataKPIs.week + '',
        weekStart: this.datePipe.transform(weekDataKPIs.weekStart, 'shortDate'),
        averageEggWeightPerLayerPerDay: this.formatValue(
          weekDataKPIs.AverageEggWeightPerLayerPerDay,
        ),
        eggCountPerLayerInProduction: this.formatValue(weekDataKPIs.EggCountPerLayerInProduction),
        eggCountPerLayerInProductionCumulative: this.formatValue(
          weekDataKPIs.EggCountPerLayerInProductionCumulative,
        ),
        feedConsumptionPerLayerPerDay: this.formatValue(weekDataKPIs.FeedConsumptionPerLayerPerDay),
        feedConsumptionPerLayerPerDayCumulative: this.formatValue(
          weekDataKPIs.FeedConsumptionPerLayerPerDayCumulative,
        ),
        feedConsumptionPerLayerInProduction: this.formatValue(
          weekDataKPIs.FeedConsumptionPerLayerInProduction,
        ),
        feedConsumptionPerLayerInProductionCumulative: this.formatValue(
          weekDataKPIs.FeedConsumptionPerLayerInProductionCumulative,
        ),
        feedConversionRate: this.formatValue(weekDataKPIs.FeedConversionRate),
        feedConversionRateCumulative: this.formatValue(weekDataKPIs.FeedConversionRateCumulative),
        feedToEggRatio: this.formatValue(weekDataKPIs.FeedToEggRatio),
        mortalityRate: this.formatValue(weekDataKPIs.MortalityRate),
        mortalityRateCumulative: this.formatValue(weekDataKPIs.MortalityRateCumulative),
        rateOfLay: this.formatValue(weekDataKPIs.RateOfLay),
        totalEggWeight: this.formatValue(weekDataKPIs.TotalEggWeight),
        totalEggWeightPerLayerInProductionCumulative: this.formatValue(
          weekDataKPIs.TotalEggWeightPerLayerInProductionCumulative,
        ),
        waterToFeedRatio: this.formatValue(weekDataKPIs.WaterToFeedRatio),
        // manual input
        averageEggWeight: this.formatValue(weekDataKPIs.AverageEggWeight),
        averageLayerWeight: this.formatValue(weekDataKPIs.AverageLayerWeight),
        feedConsumption: this.formatValue(weekDataKPIs.FeedConsumption),
        secondFeedConsumption: this.formatValue(weekDataKPIs.SecondFeedConsumption),
        mortalityCount: this.formatValue(weekDataKPIs.MortalityCount),
        totalEggCount: this.formatValue(weekDataKPIs.TotalEggCount),
        totalEggCountSecondGrade: this.formatValue(weekDataKPIs.TotalEggCountSecondGrade),
        waterConsumption: this.formatValue(weekDataKPIs.WaterConsumption),
        moult:
          typeof flockWeekData.isInMoult === 'boolean'
            ? this.translate('week-registration', `moult-${flockWeekData.isInMoult}`)
            : null,
        remarks: flockWeekData.remarks.join(' '),
      };
    });
  }

  openBenchmark(kpiItem: string): void {
    this.store.dispatch(
      flockActions.loadFlockBenchmarks({
        flock: this.flock,
        kpi: {
          benchmark: {
            benchmarkCode: kpiItem,
          },
          name: kpiItem as WeekRegistrationKPIEnum,
          unit: null,
        },
      }),
    );
  }

  onWeekRegistrationSortByChanged(ev: MatSelectChange) {
    this.weekRegistrationSortBy = ev.value as string;

    this.refreshTable();
  }

  private formatValue(kpiItem: WeekRegistrationKPIItem): string {
    let value;
    if (!kpiItem) {
      return '';
    }

    if (FlockOverviewWeekRegistrationDetailedComponent.isNaN(kpiItem.value)) {
      value = '-';
    } else {
      switch (kpiItem.name) {
        case WeekRegistrationKPIEnum.MortalityRate:
        case WeekRegistrationKPIEnum.MortalityRateCumulative:
        case WeekRegistrationKPIEnum.RateOfLay:
        case WeekRegistrationKPIEnum.AverageEggWeightPerLayerPerDay:
        case WeekRegistrationKPIEnum.EggCountPerLayerInProduction:
        case WeekRegistrationKPIEnum.EggCountPerLayerInProductionCumulative:
        case WeekRegistrationKPIEnum.FeedConsumptionPerLayerPerDay:
        case WeekRegistrationKPIEnum.FeedConsumptionPerLayerInProduction:
        case WeekRegistrationKPIEnum.FeedConsumptionPerLayerInProductionCumulative:
        case WeekRegistrationKPIEnum.FeedConsumptionPerLayerPerDayCumulative:
        case WeekRegistrationKPIEnum.FeedToEggRatio:
          value = this.decimalPipe.transform(kpiItem.value, '1.1-1', this.locale);
          break;

        case WeekRegistrationKPIEnum.AverageEggWeight:
        case WeekRegistrationKPIEnum.TotalEggWeight:
        case WeekRegistrationKPIEnum.TotalEggWeightPerLayerInProductionCumulative:
        case WeekRegistrationKPIEnum.WaterToFeedRatio:
        case WeekRegistrationKPIEnum.FeedConversionRate:
        case WeekRegistrationKPIEnum.FeedConversionRateCumulative:
          value = this.decimalPipe.transform(kpiItem.value, '1.2-2', this.locale);
          break;

        case WeekRegistrationKPIEnum.AverageLayerWeight:
        case WeekRegistrationKPIEnum.MortalityCount:
        case WeekRegistrationKPIEnum.TotalEggCount:
        case WeekRegistrationKPIEnum.TotalEggCountSecondGrade:
        case WeekRegistrationKPIEnum.SecondFeedConsumption:
        case WeekRegistrationKPIEnum.FeedConsumption:
        case WeekRegistrationKPIEnum.WaterConsumption:
          value = this.decimalPipe.transform(kpiItem.value, '1.0-0', this.locale);
          break;

        default:
          break;
      }
    }

    return value;
  }

  private static isNaN(value: number): boolean {
    return Number.isNaN(value) || !Number.isFinite(value);
  }

  private translate(prefixKey: string, key: string): string {
    return this.localeService.translate(`${prefixKey}.${key}`);
  }
}
